import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Bell, CheckCircle2, Share, PlusSquare, Smartphone, X, Loader2, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import {
  setPushPromptDismissed,
  subscribeCurrentDeviceToPush,
  syncOrAutoSubscribePushOnLogin,
  isWebPushSupportedInBrowser,
  getBrowserPushPermissionState,
} from '../../lib/webPushClient';

export const AutoPushSubscriptionPrompt: React.FC = () => {
  const { currentUser, authLoading, currentView } = useApp();
  const [promptMode, setPromptMode] = useState<
    'hidden' | 'enable_prompt' | 'ios_home_screen' | 'enabled_toast'
  >('hidden');
  const [subscribing, setSubscribing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const toastTimeoutRef = useRef<number | null>(null);
  const dismissTimeoutRef = useRef<number | null>(null);

  // Clear pending timers on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) window.clearTimeout(toastTimeoutRef.current);
      if (dismissTimeoutRef.current) window.clearTimeout(dismissTimeoutRef.current);
    };
  }, []);

  const isEligibleUser = Boolean(
    !authLoading &&
      currentUser?.id &&
      (currentUser.role === 'customer' || currentUser.role === 'professional')
  );

  const runLoginPushSync = useCallback(async () => {
    if (!currentUser?.id || (currentUser.role !== 'customer' && currentUser.role !== 'professional')) {
      setPromptMode('hidden');
      return;
    }

    const res = await syncOrAutoSubscribePushOnLogin({
      userId: currentUser.id,
      role: currentUser.role,
      email: currentUser.email,
    });

    if (res.action === 'show_prompt') {
      setPromptMode('enable_prompt');
    } else if (res.action === 'needs_ios_install') {
      setPromptMode('ios_home_screen');
    } else {
      setPromptMode('hidden');
    }
  }, [currentUser?.id, currentUser?.role, currentUser?.email]);

  useEffect(() => {
    if (!isEligibleUser) {
      setPromptMode('hidden');
      setErrorMessage(null);
      return;
    }
    void runLoginPushSync();
  }, [isEligibleUser, runLoginPushSync]);

  // Hide prompt if user enables or disables push from the dashboard settings card
  useEffect(() => {
    const handlePushStatusUpdated = () => {
      setPromptMode((prev) => (prev === 'enabled_toast' ? prev : 'hidden'));
    };
    window.addEventListener('jobmatch:push-status-updated', handlePushStatusUpdated);
    return () => {
      window.removeEventListener('jobmatch:push-status-updated', handlePushStatusUpdated);
    };
  }, []);

  if (!isEligibleUser || promptMode === 'hidden') {
    return null;
  }

  // Avoid overlapping auth screens while transitioning
  if (
    currentView === 'customer_auth' ||
    currentView === 'pro_auth' ||
    currentView === 'admin_auth' ||
    currentView === 'password_reset'
  ) {
    return null;
  }

  const userRole = currentUser!.role as 'customer' | 'professional';

  /**
   * Handle "Enable" click:
   * 1. Checks browser support for Web Push and Notifications API.
   * 2. Directly requests browser notification permission on the active user gesture.
   * 3. Upon permission 'granted', runs the complete Web Push subscription workflow.
   * 4. Updates UI with success toast or appropriate error feedback.
   */
  const handleEnableClick = async () => {
    if (!currentUser?.id || subscribing) return;

    if (dismissTimeoutRef.current) {
      window.clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }

    setSubscribing(true);
    setErrorMessage(null);

    // Verify Notification and Web Push API availability in the current browser
    if (typeof window === 'undefined' || !('Notification' in window) || !isWebPushSupportedInBrowser()) {
      setErrorMessage('Web Push notifications are not supported in this browser or environment.');
      setSubscribing(false);
      return;
    }

    try {
      // 1. Directly request browser permission on the active user interaction
      let currentPermission: NotificationPermission = Notification.permission;

      if (currentPermission === 'default') {
        try {
          const req = Notification.requestPermission();
          if (req && typeof (req as Promise<NotificationPermission>).then === 'function') {
            currentPermission = await req;
          } else {
            // Older browser callback compatibility
            currentPermission = await new Promise<NotificationPermission>((resolve) => {
              Notification.requestPermission(resolve);
            });
          }
        } catch (permErr: any) {
          console.warn('[AutoPush] Browser permission request failed:', permErr);
          currentPermission = Notification.permission;
        }
      }

      // 2. Handle denied / blocked permission
      if (currentPermission === 'denied') {
        setPushPromptDismissed(currentUser.id, true);
        setErrorMessage(
          'Notifications are blocked in your browser settings. To enable them, click the lock/settings icon in your address bar and allow notifications for jobmatch.ng.'
        );
        dismissTimeoutRef.current = window.setTimeout(() => {
          setPromptMode('hidden');
        }, 5000);
        return;
      }

      // 3. Handle dismissed native dialog without granting
      if (currentPermission !== 'granted') {
        setErrorMessage('Notification permission was not granted. Tap Enable when you are ready.');
        return;
      }

      // 4. Permission is granted: execute the existing Web Push subscription workflow
      const result = await subscribeCurrentDeviceToPush({
        userId: currentUser.id,
        role: userRole,
        email: currentUser.email,
        autoRefresh: false,
      });

      if (result.success) {
        // Ensure prompt dismiss flag is reset since user explicitly enabled notifications
        setPushPromptDismissed(currentUser.id, false);
        setPromptMode('enabled_toast');
        toastTimeoutRef.current = window.setTimeout(() => {
          setPromptMode('hidden');
        }, 3400);
      } else if (result.permission === 'denied') {
        setPushPromptDismissed(currentUser.id, true);
        setErrorMessage(
          'Browser notification permission was blocked. Please allow notifications in site settings.'
        );
      } else {
        setErrorMessage(result.error || 'Could not enable push notifications right now. Please try again.');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'An unexpected error occurred while enabling notifications.');
    } finally {
      setSubscribing(false);
    }
  };

  /**
   * Handle "Not Now" click:
   * Dismisses the prompt and stores the user preference in local storage so they are
   * not repeatedly prompted on subsequent page loads or logins on this device.
   */
  const handleNotNowClick = () => {
    if (dismissTimeoutRef.current) {
      window.clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    if (currentUser?.id) {
      setPushPromptDismissed(currentUser.id, true);
    }
    setErrorMessage(null);
    setPromptMode('hidden');
  };

  if (promptMode === 'enabled_toast') {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="auto-push-enabled-toast"
        className="fixed bottom-20 md:bottom-6 right-3 left-3 sm:left-auto sm:right-6 sm:w-[400px] max-w-[calc(100vw-24px)] z-50 bg-emerald-950 text-white border border-emerald-700/70 rounded-2xl p-4 shadow-xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-200"
      >
        <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-extrabold tracking-tight text-white">
            Push Notifications Enabled
          </p>
          <p className="text-[11px] text-emerald-200 leading-snug mt-0.5">
            This device is now connected to receive instant JobMatch NG alerts.
          </p>
        </div>
        <button
          type="button"
          onClick={handleNotNowClick}
          aria-label="Close notification"
          className="text-emerald-300 hover:text-white p-1.5 rounded-lg hover:bg-emerald-800/40 transition cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (promptMode === 'ios_home_screen') {
    return (
      <div
        role="dialog"
        aria-modal="false"
        aria-label="Enable Notifications on iPhone"
        data-testid="auto-push-ios-prompt"
        className="fixed bottom-20 md:bottom-6 right-3 left-3 sm:left-auto sm:right-6 sm:w-[400px] max-w-[calc(100vw-24px)] z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-2xl transition-all animate-in fade-in slide-in-from-bottom-3 duration-200"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800/60 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-extrabold text-slate-900 dark:text-white tracking-tight">
                Enable Notifications on iPhone
              </h3>
              <button
                type="button"
                onClick={handleNotNowClick}
                aria-label="Dismiss prompt"
                data-testid="auto-push-ios-dismiss-btn"
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
              On iPhone (iOS 16.4+), install JobMatch NG via{' '}
              <span className="font-bold text-slate-900 dark:text-white inline-flex items-center gap-1">
                <Share className="w-3 h-3 inline text-amber-600 dark:text-amber-400" /> Share
              </span>{' '}
              &rarr;{' '}
              <span className="font-bold text-slate-900 dark:text-white inline-flex items-center gap-1">
                <PlusSquare className="w-3 h-3 inline text-amber-600 dark:text-amber-400" /> Add to Home Screen
              </span>
              , then open the app from your Home Screen to enable push alerts.
            </p>
            <div className="flex items-center justify-end gap-2 mt-3.5">
              <button
                type="button"
                onClick={handleNotNowClick}
                data-testid="auto-push-ios-not-now-btn"
                className="w-full sm:w-auto px-4 py-2 sm:py-1.5 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 transition cursor-pointer text-center"
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Enable Notifications"
      aria-busy={subscribing}
      data-testid="auto-push-prompt"
      className="fixed bottom-20 md:bottom-6 right-3 left-3 sm:left-auto sm:right-6 sm:w-[400px] max-w-[calc(100vw-24px)] z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-2xl transition-all animate-in fade-in slide-in-from-bottom-3 duration-200"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
          <Bell className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-extrabold text-slate-900 dark:text-white tracking-tight">
              Enable Notifications
            </h3>
            <button
              type="button"
              onClick={handleNotNowClick}
              disabled={subscribing}
              aria-label="Dismiss prompt"
              data-testid="auto-push-dismiss-btn"
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer disabled:opacity-40"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
            {userRole === 'professional'
              ? 'Get instant browser alerts for new matching jobs in your area, customer messages, accepted quotes, and payouts.'
              : 'Get instant browser alerts when verified professionals send quotes, reply to messages, or update your job status.'}
          </p>

          {errorMessage && (
            <div className="mt-2.5 p-2 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900/60 flex items-start gap-1.5 text-rose-700 dark:text-rose-300 text-[11px] leading-snug">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
              <span className="flex-1 font-semibold">{errorMessage}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              disabled={subscribing}
              onClick={handleNotNowClick}
              data-testid="auto-push-not-now-btn"
              className="flex-1 sm:flex-none px-4 py-2 sm:py-1.5 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 transition cursor-pointer disabled:opacity-50 text-center"
            >
              Not Now
            </button>
            <button
              type="button"
              disabled={subscribing}
              onClick={handleEnableClick}
              data-testid="auto-push-enable-btn"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 sm:py-1.5 rounded-xl text-xs font-extrabold bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 disabled:opacity-60 text-white shadow-sm transition cursor-pointer text-center"
            >
              {subscribing && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />}
              <span>{subscribing ? 'Enabling...' : 'Enable Notifications'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
